import { createContext } from 'react';
import { AnyFieldApi, createFormHookContexts } from '@tanstack/react-form';
import { z } from 'zod';

export const { fieldContext, formContext, useFieldContext, useFormContext } =
  createFormHookContexts();

interface FieldErrorMeta {
  isTouched: boolean;
  errors: readonly { message: string }[];
}

export const revealErrorsContext = createContext(false);

export const getFieldErrorMessage = (
  { isTouched, errors }: FieldErrorMeta,
  revealErrors = false
): string | undefined => (isTouched || revealErrors ? errors[0]?.message : undefined);

// One form-level mount error keeps Save disabled until the first touch, when form-core drops it.
export const schemaValidators = <TSchema extends z.ZodType>(
  schema: TSchema
): { onMount: (props: { value: unknown }) => string | undefined; onChange: TSchema } => ({
  onMount: ({ value }) => (schema.safeParse(value).success ? undefined : 'Form is invalid'),
  onChange: schema
});

// Dropdown and date fields can only hold values from their options, so their schemas carry the
// domain type without re-checking it. Those fields store null when cleared.
export const requiredChoice = <T>(message: string) =>
  z.custom<T | null>().transform((value, ctx): T => {
    if (value === null) {
      ctx.addIssue({ code: 'custom', message });
      return z.NEVER;
    }
    return value;
  });

export const optionalChoice = <T>() => z.custom<T | null>().transform(value => value ?? undefined);

export const requiredText = (message: string) => z.string().trim().min(1, message);

export const blankTo = <TEmpty extends null | undefined>(empty: TEmpty) =>
  z
    .string()
    .trim()
    .transform((value): string | TEmpty => value || empty);

// `handleBlur` runs only onBlur validators, and the first touch drops the mount error,
// so without the change run a field left empty on blur would count as valid.
export const touchField = (field: AnyFieldApi): void => {
  field.handleBlur();
  field.validate('change');
};
